/*
 * Command dialog. Renders type-aware input for a single writable Leaf,
 * shows sensitivity / safety warnings, and calls the backend when the user
 * confirms.
 *
 * Concept refs:
 *  - C.5: no optimistic UI — we only mark the command as Sent (and start the
 *    correlation-id lifecycle) after the endpoint returns 202.
 *  - C.6: Force / Override goes through the same command path with a stricter
 *    gate (`plants.control.force` + `forceAllowed` + `!safetyMarker`). The
 *    UI exposes it as a second row in this modal so operators pick the right
 *    verb consciously, not by accident.
 *  - D.5: sensitivity ladder + safety marker drive the confirmation banners.
 *    For Critical writes we require the user to re-type the leaf's symbol,
 *    matching how the reference tool prompts before hazardous writes.
 *  - B.7: buttons on the leaf row appear only for ReadWrite / WriteOnly leaves;
 *    the modal double-checks the same rule so a stale render can't bypass it.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { api, ApiError } from '../api/client';
import type { LeafDto, SendCommandMode, SendCommandRequest, SendCommandResponse } from '../api/types';
import { trackCommand } from '../realtime/commandsStore';
import { registerWrite } from '../realtime/writeStore';
import { useLivePlant } from '../realtime/signalStore';
import { HeroSendCommand, IconCancel } from '../icons/HvacIcons';
import './SendCommandModal.css';

interface Props {
  plantId: string;
  leaf: LeafDto;
  onClose: () => void;
}

type ParseResult =
  | { ok: true; value: unknown; label: string }
  | { ok: false; error: string };

export function SendCommandModal({ plantId, leaf, onClose }: Props) {
  const isWritable = leaf.writability === 'ReadWrite' || leaf.writability === 'WriteOnly';
  const isCritical = leaf.writeSensitivity === 'Critical';
  const isElevated = leaf.writeSensitivity === 'Elevated';

  // C.6: force toggle enables force mode; the toggle itself is only rendered
  // for leaves where the schema opts in. Safety-flagged leaves show the toggle
  // in a disabled state with an explanation — hiding it entirely would hide
  // *why* force is unavailable and confuse the operator.
  const forceToggleVisible = leaf.forceAllowed || leaf.safetyMarker;
  const forceToggleDisabled = !leaf.forceAllowed || leaf.safetyMarker;

  const [forceMode, setForceMode] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const [forceUntil, setForceUntil] = useState('');

  // Pull the current live value for the header — nice-to-have context so the
  // operator sees what they're about to overwrite. Also drives the
  // "Force lösen" affordance: only visible while quality === 'Override'.
  const live = useLivePlant(plantId);
  const valueSignal = live?.leafSignal(leaf.id) ?? null;
  const liveNow = valueSignal ? valueSignal.value : null;
  const isCurrentlyOverridden = liveNow?.q === 'Override';

  const [rawValue, setRawValue] = useState<string>(() => defaultRaw(leaf));
  const [flagsValue, setFlagsValue] = useState<Set<number>>(() => new Set());
  const [confirmSymbol, setConfirmSymbol] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submittingClear, setSubmittingClear] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ESC closes; overlay click closes. Focus the first field on open.
  const firstFieldRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parsed = useMemo<ParseResult>(
    () => parseValue(leaf, rawValue, flagsValue),
    [leaf, rawValue, flagsValue],
  );

  const criticalOk = !isCritical || confirmSymbol.trim() === leaf.symbol;
  const canSubmit = isWritable && parsed.ok && criticalOk && !submitting && !submittingClear;

  async function onSubmit(e: JSX.TargetedEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!parsed.ok || !canSubmit) return;

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const mode: SendCommandMode = forceMode ? 'Force' : 'Normal';
      const body: SendCommandRequest = { value: parsed.value, mode };
      if (forceMode) {
        const trimmedReason = forceReason.trim();
        if (trimmedReason !== '') body.reason = trimmedReason;
        if (forceUntil !== '') {
          // datetime-local returns e.g. "2026-07-27T14:00" — turn into ISO
          // with the browser's local offset so the audit row records the
          // wall-clock the operator saw. Invalid inputs are dropped.
          const parsedDate = new Date(forceUntil);
          if (!Number.isNaN(parsedDate.getTime())) {
            body.until = parsedDate.toISOString();
          }
        }
      }
      // Command name "set" is the setpoint-style write. The URL shape is
      // /api/plants/{plantId}/leaves/{leafId}/commands/{name} — see
      // SendCommandEndpoint.cs.
      const response = await api.post<SendCommandResponse>(
        `/api/plants/${plantId}/leaves/${leaf.id}/commands/set`,
        body,
      );

      trackCommand({
        correlationId: response.correlationId,
        plantId,
        leafId: leaf.id,
        leafSymbol: leaf.displayName ?? leaf.symbol,
        valueLabel: forceMode ? `FORCE ${parsed.label}` : parsed.label,
      });
      // Row-Anzeige: writeStore trackt die Zwei-Stufen-Bestätigung (Ack +
      // Readback) und dekoriert die Leaf-Zeile mit Farb-Highlight + alt→neu-
      // Vorschau bis der Regler den neuen Wert zurückmeldet.
      registerWrite({
        leafId: leaf.id,
        correlationId: response.correlationId,
        sentValue: parsed.value,
      });

      onClose();
    } catch (err) {
      const msg = extractErrorMessage(err);
      setErrorMessage(msg);
      setSubmitting(false);
    }
  }

  async function onForceClear() {
    if (submitting || submittingClear) return;
    setSubmittingClear(true);
    setErrorMessage(null);
    try {
      // ForceClear ignores the value; we still send `null` so the endpoint
      // gets a well-formed body. The backend's ForceClear branch skips the
      // ForceAllowed / SafetyMarker checks so a schema flip can never trap
      // a leaf in Override.
      const body: SendCommandRequest = { value: null, mode: 'ForceClear' };
      const response = await api.post<SendCommandResponse>(
        `/api/plants/${plantId}/leaves/${leaf.id}/commands/set`,
        body,
      );
      trackCommand({
        correlationId: response.correlationId,
        plantId,
        leafId: leaf.id,
        leafSymbol: leaf.displayName ?? leaf.symbol,
        valueLabel: 'FORCE lösen',
      });
      onClose();
    } catch (err) {
      const msg = extractErrorMessage(err);
      setErrorMessage(msg);
      setSubmittingClear(false);
    }
  }

  const title = forceMode ? 'Wert forcen' : 'Wert schreiben';
  const submitLabel = forceMode ? 'Force setzen' : 'Senden';
  const submitBusyLabel = forceMode ? 'Force setzen…' : 'Senden…';

  return (
    <div class="hv-cmd" role="dialog" aria-modal="true" aria-labelledby="hv-cmd-title" onClick={onClose}>
      <div class="hv-cmd__card" onClick={(e) => e.stopPropagation()}>
        <header class="hv-cmd__header">
          <h2 id="hv-cmd-title" class="hv-cmd__title">
            {title}
          </h2>
          <button class="hv-cmd__close" type="button" aria-label="Schließen" onClick={onClose}>×</button>
        </header>

        <div class="hv-modal__hero">
          <HeroSendCommand size={72} style={{ color: 'var(--accent)' }} />
        </div>

        <div class="hv-cmd__meta">
          <div class="hv-cmd__meta-row">
            <span class="hv-cmd__meta-label">Datenpunkt</span>
            <span class="hv-cmd__meta-value">
              {leaf.displayName ?? leaf.symbol} <span class="mono muted">({leaf.symbol})</span>
            </span>
          </div>
          <div class="hv-cmd__meta-row">
            <span class="hv-cmd__meta-label">Typ</span>
            <span class="hv-cmd__meta-value">
              {leaf.valueType}{leaf.unit ? ` [${leaf.unit}]` : ''}
            </span>
          </div>
          <div class="hv-cmd__meta-row">
            <span class="hv-cmd__meta-label">Aktueller Wert</span>
            <span class="hv-cmd__meta-value">
              {liveNow ? String(liveNow.v) : '—'}
              {liveNow && leaf.unit ? ` ${leaf.unit}` : ''}
              {isCurrentlyOverridden && (
                <span class="hv-cmd__override-tag">Force aktiv</span>
              )}
            </span>
          </div>
        </div>

        {forceToggleVisible && (
          <div class="hv-cmd__force-row">
            <label class="hv-cmd__force-toggle">
              <input
                type="checkbox"
                checked={forceMode}
                disabled={forceToggleDisabled}
                onChange={(e) => setForceMode((e.currentTarget as HTMLInputElement).checked)}
              />
              <span>Force-Modus</span>
            </label>
            {leaf.safetyMarker && (
              <span class="hv-cmd__force-hint hv-cmd__force-hint--error">
                Safety-relevant — Force verboten
              </span>
            )}
            {!leaf.safetyMarker && !leaf.forceAllowed && (
              <span class="hv-cmd__force-hint">
                Force für diesen Datenpunkt nicht freigegeben
              </span>
            )}
            {forceMode && (
              <span class="hv-cmd__force-hint hv-cmd__force-hint--warn">
                Command höchster Kategorie
              </span>
            )}
          </div>
        )}

        {isCritical && (
          <div class="hv-cmd__banner hv-cmd__banner--critical" role="alert">
            <strong>Kritischer Wert.</strong> Bitte doppelt prüfen. Zur Bestätigung Symbol{' '}
            <code class="mono">{leaf.symbol}</code> unten eintippen.
          </div>
        )}
        {isElevated && (
          <div class="hv-cmd__banner hv-cmd__banner--elevated" role="alert">
            Erhöhte Berechtigung erforderlich.
          </div>
        )}
        {leaf.safetyMarker && (
          <div class="hv-cmd__banner hv-cmd__banner--safety" role="alert">
            Sicherheitsrelevanter Datenpunkt — Betätigung wird auditiert.
          </div>
        )}
        {forceMode && (
          <div class="hv-cmd__banner hv-cmd__banner--force" role="alert">
            <strong>Force überschreibt die Steuerungslogik.</strong> Vergiss nicht,
            das Force später aufzuheben.
          </div>
        )}

        <form class="hv-cmd__form" onSubmit={onSubmit}>
          <ValueEditor
            leaf={leaf}
            rawValue={rawValue}
            setRawValue={setRawValue}
            flagsValue={flagsValue}
            setFlagsValue={setFlagsValue}
            firstFieldRef={firstFieldRef}
          />

          {!parsed.ok && (
            <p class="hv-cmd__hint hv-cmd__hint--error">{parsed.error}</p>
          )}

          {isCritical && (
            <label class="hv-cmd__field">
              <span class="hv-cmd__label">
                Bestätigung — Symbol <code class="mono">{leaf.symbol}</code> eintippen
              </span>
              <input
                class="hv-cmd__input"
                type="text"
                value={confirmSymbol}
                onInput={(e) => setConfirmSymbol((e.currentTarget as HTMLInputElement).value)}
                autoComplete="off"
                spellcheck={false}
              />
            </label>
          )}

          {forceMode && (
            <>
              <label class="hv-cmd__field">
                <span class="hv-cmd__label">Grund (optional)</span>
                <textarea
                  class="hv-cmd__input hv-cmd__textarea"
                  value={forceReason}
                  onInput={(e) => setForceReason((e.currentTarget as HTMLTextAreaElement).value)}
                  rows={2}
                  maxLength={500}
                  placeholder="z. B. Wartung Kessel — Regelung deaktiviert"
                />
              </label>
              <label class="hv-cmd__field">
                <span class="hv-cmd__label">Automatisch entforcen um… (optional)</span>
                <input
                  class="hv-cmd__input"
                  type="datetime-local"
                  value={forceUntil}
                  onInput={(e) => setForceUntil((e.currentTarget as HTMLInputElement).value)}
                />
                <span class="hv-cmd__hint">
                  Wird an den Treiber übergeben. Die serverseitige Auto-Freigabe
                  ist noch nicht implementiert (F.10).
                </span>
              </label>
            </>
          )}

          {errorMessage && (
            <p class="hv-cmd__error" role="alert">{errorMessage}</p>
          )}

          <footer class="hv-cmd__footer">
            {isCurrentlyOverridden && (
              <button
                type="button"
                class="hv-btn hv-btn--warn hv-btn--md hv-cmd__force-clear"
                onClick={onForceClear}
                disabled={submitting || submittingClear}
              >
                {submittingClear ? 'Force lösen…' : 'Force lösen'}
              </button>
            )}
            {/* Use native buttons here: Button component's Props type erases
                `type`/`disabled` under the current tsconfig (pre-existing shape
                bug); we bypass it to keep the modal self-contained. */}
            <button type="button" class="hv-btn hv-btn--ghost hv-btn--md" onClick={onClose}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
            </button>
            <button
              type="submit"
              class={`hv-btn hv-btn--md ${forceMode ? 'hv-btn--danger' : 'hv-btn--primary'}`}
              disabled={!canSubmit}
            >
              {submitting ? submitBusyLabel : submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// --- Value editor ---------------------------------------------------------

interface EditorProps {
  leaf: LeafDto;
  rawValue: string;
  setRawValue: (v: string) => void;
  flagsValue: Set<number>;
  setFlagsValue: (v: Set<number>) => void;
  firstFieldRef: { current: HTMLElement | null };
}

function ValueEditor({
  leaf,
  rawValue,
  setRawValue,
  flagsValue,
  setFlagsValue,
  firstFieldRef,
}: EditorProps) {
  switch (leaf.valueType) {
    case 'Bool':
    case 'Boolean': {
      const isOn = rawValue === 'true';
      return (
        <div class="hv-cmd__field">
          <span class="hv-cmd__label">Neuer Wert</span>
          <div class="hv-cmd__toggle" role="group" aria-label="Wert">
            <button
              ref={(el) => { if (!firstFieldRef.current) firstFieldRef.current = el as HTMLElement | null; }}
              type="button"
              class={`hv-cmd__toggle-btn${isOn ? ' hv-cmd__toggle-btn--active' : ''}`}
              onClick={() => setRawValue('true')}
            >
              AN
            </button>
            <button
              type="button"
              class={`hv-cmd__toggle-btn${!isOn ? ' hv-cmd__toggle-btn--active' : ''}`}
              onClick={() => setRawValue('false')}
            >
              AUS
            </button>
          </div>
        </div>
      );
    }
    case 'Enum':
    case 'Enumeration': {
      const entries = leaf.enumEntries ?? [];
      return (
        <label class="hv-cmd__field">
          <span class="hv-cmd__label">Neuer Wert</span>
          <select
            ref={(el) => { firstFieldRef.current = el as HTMLElement | null; }}
            class="hv-cmd__input"
            value={rawValue}
            onChange={(e) => setRawValue((e.currentTarget as HTMLSelectElement).value)}
          >
            <option value="" disabled>Auswählen…</option>
            {entries.map((entry) => (
              <option key={entry.value} value={String(entry.value)}>
                {entry.displayName ?? entry.name} ({entry.value})
              </option>
            ))}
          </select>
        </label>
      );
    }
    case 'Flags':
    case 'BitFlags': {
      const bits = leaf.flagBits ?? [];
      return (
        <div class="hv-cmd__field">
          <span class="hv-cmd__label">Neue Flags</span>
          <div class="hv-cmd__flags">
            {bits.map((b, idx) => (
              <label key={b.bit} class="hv-cmd__flag">
                <input
                  ref={(el) => { if (idx === 0) firstFieldRef.current = el as HTMLElement | null; }}
                  type="checkbox"
                  checked={flagsValue.has(b.bit)}
                  onChange={(e) => {
                    const next = new Set(flagsValue);
                    if ((e.currentTarget as HTMLInputElement).checked) next.add(b.bit);
                    else next.delete(b.bit);
                    setFlagsValue(next);
                  }}
                />
                <span>{b.displayName ?? b.name} <span class="muted">(bit {b.bit})</span></span>
              </label>
            ))}
            {bits.length === 0 && (
              <p class="muted">Keine Flag-Definitionen für diesen Punkt.</p>
            )}
          </div>
        </div>
      );
    }
    case 'String':
    case 'Text': {
      return (
        <label class="hv-cmd__field">
          <span class="hv-cmd__label">Neuer Wert</span>
          <input
            ref={(el) => { firstFieldRef.current = el as HTMLElement | null; }}
            class="hv-cmd__input"
            type="text"
            value={rawValue}
            maxLength={leaf.maxLength ?? undefined}
            onInput={(e) => setRawValue((e.currentTarget as HTMLInputElement).value)}
          />
        </label>
      );
    }
    default: {
      // Float / Int / anything numeric.
      const isInt = leaf.valueType === 'Int' || leaf.valueType === 'Integer';
      const step = isInt ? 1 : stepFromDecimals(leaf.decimals);
      return (
        <label class="hv-cmd__field">
          <span class="hv-cmd__label">Neuer Wert{leaf.unit ? ` (${leaf.unit})` : ''}</span>
          <input
            ref={(el) => { firstFieldRef.current = el as HTMLElement | null; }}
            class="hv-cmd__input"
            type="number"
            value={rawValue}
            min={leaf.minValue ?? undefined}
            max={leaf.maxValue ?? undefined}
            step={step}
            onInput={(e) => setRawValue((e.currentTarget as HTMLInputElement).value)}
          />
          {(leaf.minValue !== null || leaf.maxValue !== null) && (
            <span class="hv-cmd__hint">
              Bereich: {leaf.minValue ?? '−∞'} … {leaf.maxValue ?? '+∞'}
            </span>
          )}
        </label>
      );
    }
  }
}

// --- Value parsing --------------------------------------------------------

function parseValue(leaf: LeafDto, raw: string, flags: Set<number>): ParseResult {
  switch (leaf.valueType) {
    case 'Bool':
    case 'Boolean': {
      if (raw !== 'true' && raw !== 'false') return { ok: false, error: 'Bitte AN oder AUS wählen.' };
      const value = raw === 'true';
      return { ok: true, value, label: value ? 'AN' : 'AUS' };
    }
    case 'Enum':
    case 'Enumeration': {
      if (raw === '') return { ok: false, error: 'Bitte einen Wert auswählen.' };
      const n = Number(raw);
      if (!Number.isFinite(n)) return { ok: false, error: 'Ungültige Auswahl.' };
      const entry = leaf.enumEntries?.find((e) => e.value === n);
      return { ok: true, value: n, label: entry ? (entry.displayName ?? entry.name) : String(n) };
    }
    case 'Flags':
    case 'BitFlags': {
      let sum = 0;
      for (const bit of flags) sum |= 1 << bit;
      const labels = leaf.flagBits
        ?.filter((b) => flags.has(b.bit))
        .map((b) => b.displayName ?? b.name) ?? [];
      return { ok: true, value: sum, label: labels.length === 0 ? '∅' : labels.join(', ') };
    }
    case 'String':
    case 'Text': {
      if (leaf.maxLength !== null && raw.length > leaf.maxLength) {
        return { ok: false, error: `Maximal ${leaf.maxLength} Zeichen erlaubt.` };
      }
      return { ok: true, value: raw, label: raw === '' ? '(leer)' : raw };
    }
    default: {
      if (raw === '' || raw === '-' || raw === '+') return { ok: false, error: 'Bitte einen Wert eingeben.' };
      const n = Number(raw);
      if (!Number.isFinite(n)) return { ok: false, error: 'Keine gültige Zahl.' };
      const isInt = leaf.valueType === 'Int' || leaf.valueType === 'Integer';
      if (isInt && !Number.isInteger(n)) {
        return { ok: false, error: 'Bitte einen ganzzahligen Wert eingeben.' };
      }
      if (leaf.minValue !== null && n < leaf.minValue) {
        return { ok: false, error: `Mindestens ${leaf.minValue}.` };
      }
      if (leaf.maxValue !== null && n > leaf.maxValue) {
        return { ok: false, error: `Höchstens ${leaf.maxValue}.` };
      }
      const label = leaf.decimals !== null && !isInt
        ? n.toFixed(leaf.decimals)
        : String(n);
      return { ok: true, value: n, label: leaf.unit ? `${label} ${leaf.unit}` : label };
    }
  }
}

function defaultRaw(leaf: LeafDto): string {
  switch (leaf.valueType) {
    case 'Bool':
    case 'Boolean':
      return 'false';
    case 'Enum':
    case 'Enumeration':
      return '';
    case 'String':
    case 'Text':
      return '';
    default:
      return '';
  }
}

function stepFromDecimals(decimals: number | null): string {
  if (decimals === null || decimals <= 0) return 'any';
  return `0.${'0'.repeat(decimals - 1)}1`;
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // The endpoint's Results.BadRequest / Results.Json paths return `{ error: string }`.
    if (err.body && typeof err.body === 'object' && 'error' in err.body) {
      const inner = (err.body as { error: unknown }).error;
      if (typeof inner === 'string') return inner;
    }
    switch (err.status) {
      case 400: return 'Ungültige Anfrage.';
      case 401: return 'Nicht angemeldet.';
      case 403: return 'Berechtigung fehlt (Sensitivität, Safety oder Force).';
      case 404: return 'Datenpunkt nicht gefunden.';
      default: return `HTTP ${err.status}`;
    }
  }
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler.';
}
